import { useStations } from '@app/core/stations/hooks/useStations';
import RadioIcon from '@mui/icons-material/Radio';
import TvIcon from '@mui/icons-material/Tv';
import YouTubeIcon from '@mui/icons-material/YouTube';
import { ToggleButtonGroup } from '@mui/material';
import { StatusFilterToggleButton } from './StatusFilterToggleButton';
import { useMemo } from 'react';
import { defineMessage, useIntl } from 'react-intl';
import { getStationTypeFilterType } from '../../helpers/get-station-type-filter-type';
import { useStatusQueryFilter } from '../../hooks/useStatusQueryFilter';
import { StationTypeFilterType, stationTypeMessages } from '../options';

const stationTypeGroupMessage = defineMessage({
  id: 'logger.statusScreen.stationType',
  defaultMessage: 'Tipo de emisora',
});

const STATION_TYPE_FILTERS = [
  {
    type: StationTypeFilterType.RADIO,
    icon: RadioIcon,
    message: stationTypeMessages.radio,
  },
  {
    type: StationTypeFilterType.TV,
    icon: TvIcon,
    message: stationTypeMessages.tv,
  },
  {
    type: StationTypeFilterType.YOUTUBE,
    icon: YouTubeIcon,
    message: stationTypeMessages.youtube,
  },
] as const;

const ALL_STATION_TYPE_FILTERS = STATION_TYPE_FILTERS.map(({ type }) => type);

/**
 * Non-exclusive toggle group for the station types the user actually has.
 * No type param = show all (all available types selected in UI).
 * Renders nothing when the user has only one station type among their stations.
 */
export function StatusStationTypeFilter() {
  const { $t } = useIntl();
  const stations = useStations();
  const { queryParams, setQueryParams } = useStatusQueryFilter();

  const availableStationTypes = useMemo((): StationTypeFilterType[] => {
    const types = new Set(stations.map(getStationTypeFilterType));

    return ALL_STATION_TYPE_FILTERS.filter((type) => types.has(type));
  }, [stations]);

  const counts = useMemo(() => {
    const countsByType: Record<StationTypeFilterType, number> = {
      [StationTypeFilterType.RADIO]: 0,
      [StationTypeFilterType.TV]: 0,
      [StationTypeFilterType.YOUTUBE]: 0,
    };

    for (const station of stations) {
      countsByType[getStationTypeFilterType(station)]++;
    }

    return countsByType;
  }, [stations]);

  const selectedTypes = useMemo(() => {
    if (queryParams.type == null) {
      return availableStationTypes;
    }

    const filteredSelection = queryParams.type.filter((type) =>
      availableStationTypes.includes(type),
    );

    return filteredSelection.length > 0 ? filteredSelection : availableStationTypes;
  }, [queryParams.type, availableStationTypes]);

  if (availableStationTypes.length <= 1) {
    return null;
  }

  const handleChange = (
    _event: React.MouseEvent<HTMLElement>,
    newValue: StationTypeFilterType[],
  ) => {
    if (newValue.length === 0 || newValue.length === availableStationTypes.length) {
      setQueryParams({ type: null });
    } else {
      setQueryParams({ type: newValue });
    }
  };

  return (
    <ToggleButtonGroup
      value={selectedTypes}
      onChange={handleChange}
      aria-label={$t(stationTypeGroupMessage)}
      size="small"
    >
      {STATION_TYPE_FILTERS.filter(({ type }) => availableStationTypes.includes(type)).map(
        ({ type, icon, message }) => (
          <StatusFilterToggleButton
            key={type}
            value={type}
            icon={icon}
            label={$t(message)}
            count={counts[type]}
            color="info"
          />
        ),
      )}
    </ToggleButtonGroup>
  );
}
